import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { BuildingContactRoleView } from '@api-proxies';
import { BuildingContactRoleService } from '@http/clients/building-contact-role.service';
import { SUPPORTED_OPPORTUNITY_COMPANY_ROLE_IDS } from '@models/OpportunityCompanyRoleIds';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import {
    EditorItemComponent,
    IComponentFactory,
    IEditorItemConfig,
    IconSanitizerService,
    ModalComponentBase
} from 'ngx-lib';
import {
    CompanyRolesEditorContext,
    CompanyRolesDropdownEditorComponent
} from './company-roles-dropdown-editor.component';

@Component({
    selector: 'app-modal-select-company-roles',
    templateUrl: './modal-select-company-roles.component.html',
    styleUrls: ['./modal-select-company-roles.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [...CDK_IMPORTS, EditorItemComponent],
    host: {
        '(document:keydown)': 'onKeydownHandler($event)'
    }
})
export class ModalSelectCompanyRolesComponent extends ModalComponentBase<undefined, number[]> implements OnInit {
    private readonly buildingContactRoleService = inject(BuildingContactRoleService);
    private readonly iconSanitizer = inject(IconSanitizerService);

    readonly state: CompanyRolesEditorContext = {
        roleOptions: [],
        selectedRoles: [],
        showValidationError: false,
        isLoading: false,
        hasLoadError: false,
        dropdownConfig: {
            multiple: true,
            defaultText: 'Select Company Roles',
            itemDisplayKey: 'name'
        }
    };

    readonly rolesEditorItemConfig: IEditorItemConfig = {
        label: 'Company Roles',
        componentId: 'company-roles-dropdown',
        fieldName: 'selectedRoles',
        bindContext: this.state
    };

    readonly rolesEditorFactory: IComponentFactory = {
        getComponent: () => CompanyRolesDropdownEditorComponent
    };

    readonly closeIcon: SafeHtml = this.iconSanitizer.getIcon('closeModal');

    async ngOnInit(): Promise<void> {
        await this.loadRoleOptions();
    }

    async retryLoadRoleOptions(): Promise<void> {
        await this.loadRoleOptions();
    }

    onSelectedRolesChange(roles: BuildingContactRoleView[]): void {
        this.state.selectedRoles = roles;
        if (this.getValidSelectedRoleIds().length) this.state.showValidationError = false;
    }

    onContinue(): void {
        if (this.state.isLoading || this.state.hasLoadError || !this.state.roleOptions.length) return;

        const roleIds = this.getValidSelectedRoleIds();

        if (!roleIds.length) {
            this.state.showValidationError = true;
            return;
        }

        this.close(roleIds);
    }

    private getValidSelectedRoleIds(): number[] {
        return [
            ...new Set(
                this.state.selectedRoles
                    .map((role) => role.id)
                    .filter(
                        (id): id is number =>
                            typeof id === 'number' && SUPPORTED_OPPORTUNITY_COMPANY_ROLE_IDS.includes(id)
                    )
            )
        ];
    }

    private async loadRoleOptions(): Promise<void> {
        this.state.isLoading = true;
        this.state.hasLoadError = false;
        try {
            this.state.roleOptions = await this.buildingContactRoleService.getOpportunityCompanyRoles();
        } catch {
            this.state.hasLoadError = true;
        } finally {
            this.state.isLoading = false;
        }
    }
}
